import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RunnerPageDescription } from '@Client/runner.directives/common/PageDescription';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';
import { CreationHubHeader } from './CreationHubHeader';
import { TextButton } from '@Shared.Angular/flowingly.components/buttons/TextButton';
import { getService } from '@Client/runner.hooks/useService';
import {
  CreationHubProcedureDetails,
  CreationHubProcedureDetailsPanel
} from './CreationHubProcedureDetailsPanel';
import { ProcedureViewMode } from '@Shared.Angular/@types/core/contracts/constants/procedureViewMode';
import { CountdownTimer } from '@Shared.Angular/flowingly.components/CountdownTimer/CountdownTimer';
import { RecordingInProgressOverlay } from '@Shared.Angular/flowingly.components/CountdownTimer/RecordingInProgressOverlay';
import {
  RecordingPhase,
  VIDEO_RECORDING_COMPLETE,
  VIDEO_RECORDING_PHASE_CHANGED
} from '@Client/runner.services/video.recording.service';
import { FlowModelGenerationFileService } from '@Shared.Angular/flowingly.services/flowModel.generationFile.service';

export enum ProcedureCreationType {
  FromScratch = 'FromScratch',
  FromExtension = 'FromExtension',
  FromExtensionWithAI = 'FromExtensionWithAI',
  FromRecording = 'FromRecording',
  FromUpload = 'FromUpload'
}

export const ProcedureFromXPage = ({
  creationType
}: {
  creationType: ProcedureCreationType;
}) => {
  const [procedureDetails, setProcedureDetails] =
    useState<CreationHubProcedureDetails>({
      name: '',
      description: ''
    });
  const procedureDetailsRef = useRef(procedureDetails);
  procedureDetailsRef.current = procedureDetails;

  const [validationError, setValidationError] = useState<string>('');
  const [showNameExistsError, setShowNameExistsError] = useState(false);
  const [isRequestPending, setIsRequestPending] = useState(false);
  const [isFormDisabled, setIsFormDisabled] = useState(false);
  const [recordingPhase, setRecordingPhase] = useState<RecordingPhase>(
    RecordingPhase.Inactive
  );

  const $state = getService('$state');
  const procedureApiService = getService('procedureApiService');
  const proceduresService = getService('proceduresService');
  const notificationService = getService('notificationService');
  const pubsubService = getService('pubsubService');
  const videoRecordingService = getService('videoRecordingService');

  useEffect(() => {
    const subscriberId =
      'flowingly.runner.creation-hub.procedure-from-recording';
    pubsubService.subscribe<RecordingPhase>(
      VIDEO_RECORDING_PHASE_CHANGED,
      (_event, newPhase) => {
        setRecordingPhase(newPhase);
        if (newPhase === RecordingPhase.Inactive) {
          setIsFormDisabled(false);
        }
      },
      subscriberId
    );
    pubsubService.subscribe<Blob>(
      VIDEO_RECORDING_COMPLETE,
      async (_event, blob) => {
        setProcedureDetails((prev) => ({ ...prev, videoBlob: blob }));
      },
      subscriberId
    );
    return () => {
      pubsubService.unsubscribeAll(subscriberId);
    };
  }, []);

  const downloadUrl = useMemo(
    () =>
      procedureDetails.videoBlob
        ? URL.createObjectURL(procedureDetails.videoBlob)
        : null,
    [procedureDetails.videoBlob]
  );

  useEffect(() => {
    return () => {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl]);

  const fileName = useMemo(() => {
    const name = procedureDetails?.name || 'recording';
    return procedureDetails.videoBlob
      ? name.trim().replace(/[:.]/g, '-') +
          '.' +
          (procedureDetails.videoBlob.type?.split('/')[1] || 'webm')
      : null;
  }, [procedureDetails.name, procedureDetails.videoBlob]);

  const setUpdatedDetails = (details: CreationHubProcedureDetails) => {
    if (showNameExistsError) {
      setShowNameExistsError(false);
    }
    setProcedureDetails(details);
  };

  const isProcedureNameInUse = async (): Promise<boolean> => {
    setIsRequestPending(true);
    try {
      const nameIsUnique = await procedureApiService.checkIsNameUnique(
        procedureDetails.name
      );
      setIsRequestPending(false);
      if (!nameIsUnique) {
        setShowNameExistsError(true);
      }
      return !nameIsUnique;
    } catch {
      notificationService.showErrorToast(
        'Failed to check SOP name. Please try again.'
      );
      setIsRequestPending(false);
      return true;
    }
  };

  const createAndOpenProcedure = async () => {
    if (await isProcedureNameInUse()) return;
    setIsRequestPending(true);
    try {
      const procedureId = await procedureApiService.createProcedure(
        procedureDetailsRef.current
      );
      $state.go(STATE_MAPPINGS.procedure, {
        procedureId: procedureId,
        viewMode: ProcedureViewMode.Edit
      });
    } catch (error) {
      notificationService.showErrorToast(
        'Failed to create SOP. Please try again.'
      );
      setIsRequestPending(false);
    }
  };

  const createProcedureAndStartExtensionCapture = async (isWithAI: boolean) => {
    if (await isProcedureNameInUse()) return;
    setIsRequestPending(true);
    try {
      const procedureId = await procedureApiService.createProcedure(
        procedureDetailsRef.current
      );
      proceduresService.startExtensionProcedureRecording(
        procedureId,
        procedureDetailsRef.current.name,
        isWithAI
      );
      setIsFormDisabled(true);
    } catch (error) {
      notificationService.showErrorToast(
        'Failed to create SOP. Please try again.'
      );
    } finally {
      setIsRequestPending(false);
    }
  };

  const recordVideoAndCreateProcedure = async () => {
    if (await isProcedureNameInUse()) return;
    setIsFormDisabled(true);
    await videoRecordingService.acquireMedia();
    if (videoRecordingService.getRecordingPhase() === RecordingPhase.Inactive) {
      setIsFormDisabled(false);
    }
  };

  useEffect(() => {
    if (creationType !== ProcedureCreationType.FromRecording) return;
    createProcedureFromVideo(procedureDetails.videoBlob);
  }, [procedureDetails.videoBlob, creationType]);

  const uploadVideoAndCreateProcedure = async () => {
    if (await isProcedureNameInUse()) return;
    setIsFormDisabled(true);
    await createProcedureFromVideo(procedureDetails.videoBlob);
  };

  const createProcedureFromVideo = async (videoBlob: Blob | null) => {
    if (!videoBlob) {
      setIsFormDisabled(false);
      setIsRequestPending(false);
      return;
    }
    if (videoBlob.size > FlowModelGenerationFileService.maxVideoFileSizeBytes) {
      notificationService.showErrorToast(
        'Video file is too large. Please record a shorter video.'
      );
      setIsFormDisabled(false);
      setIsRequestPending(false);
      return;
    }
    const videoFile = new File([videoBlob], fileName, {
      type: videoBlob.type || 'video/webm'
    });
    setIsRequestPending(true);
    try {
      const procedureId = await procedureApiService.createProcedureFromVideo(
        videoFile,
        procedureDetailsRef.current
      );
      $state.go(STATE_MAPPINGS.procedure, {
        procedureId,
        viewMode: ProcedureViewMode.Edit
      });
    } catch {
      setIsRequestPending(false);
      setIsFormDisabled(false);
      videoRecordingService.resetPhase();
      notificationService.showErrorToast(
        'Failed to create SOP from video. Please try again.'
      );
    }
  };

  const state: {
    title: string;
    actionButtonText: string;
    actionButtonHandler?: () => void | Promise<void>;
  } = useMemo(() => {
    switch (creationType) {
      case ProcedureCreationType.FromScratch:
        return {
          title: 'Build from Scratch',
          actionButtonText: 'Create',
          actionButtonHandler: createAndOpenProcedure
        };
      case ProcedureCreationType.FromExtension:
        return {
          title: 'Capture SOP',
          actionButtonText: 'Capture',
          actionButtonHandler: () =>
            createProcedureAndStartExtensionCapture(false)
        };
      case ProcedureCreationType.FromExtensionWithAI:
        return {
          title: 'Capture SOP with AI',
          actionButtonText: 'Capture with AI',
          actionButtonHandler: () =>
            createProcedureAndStartExtensionCapture(true)
        };
      case ProcedureCreationType.FromRecording:
        return {
          title: 'Record with AI',
          actionButtonText: procedureDetails.videoBlob
            ? 'Retry upload'
            : 'Record',
          actionButtonHandler: procedureDetails.videoBlob
            ? uploadVideoAndCreateProcedure
            : recordVideoAndCreateProcedure
        };
      case ProcedureCreationType.FromUpload:
        return {
          title: 'Generate from Video',
          actionButtonText: 'Create',
          actionButtonHandler: () => uploadVideoAndCreateProcedure()
        };
    }
  }, [creationType, procedureDetails]);

  return (
    <div className="procedure-from-x-page creation-hub-page">
      {recordingPhase === RecordingPhase.Countdown && (
        <CountdownTimer
          title="Recording will start in"
          seconds={3}
          onCountdownFinished={() => videoRecordingService.startRecording()}
        />
      )}
      {recordingPhase === RecordingPhase.Recording && (
        <RecordingInProgressOverlay
          onStop={() => videoRecordingService.stopRecording()}
        />
      )}
      <RunnerPageDescription
        showText={false}
        backLinkText="Back"
        backLinkState={STATE_MAPPINGS.creationHubProcedure}
      />
      <div className="creation-hub-page__content">
        <CreationHubHeader
          title={state.title}
          description="Fill in the details to create your standard operating procedure."
        />
        <div className="creation-hub-page__content__form">
          <CreationHubProcedureDetailsPanel
            details={procedureDetails}
            onDetailsChange={setUpdatedDetails}
            isDisabled={isRequestPending || isFormDisabled}
            showNameExistsError={showNameExistsError}
            onValidationChange={setValidationError}
            showFileUpload={creationType === ProcedureCreationType.FromUpload}
          />
          {creationType !== ProcedureCreationType.FromUpload && downloadUrl && (
            <p className="procedure-from-x-page__content__download-link-container">
              <a
                href={downloadUrl}
                download={fileName}
                data-testid="record-video-download-link"
              >
                <i className="fa-light fa-download"></i> Download video
              </a>
            </p>
          )}
          <div className="creation-hub-page__content__form__actions">
            <TextButton
              text="Cancel"
              type="text-only"
              testId="procedure-from-x-cancel-button"
              disabled={isRequestPending}
              onClick={() => {
                $state.go(STATE_MAPPINGS.creationHubProcedure);
              }}
            />
            <TextButton
              text={state.actionButtonText}
              type="primary"
              testId="procedure-from-x-create-button"
              disabled={
                !!validationError ||
                isRequestPending ||
                isFormDisabled ||
                (creationType === ProcedureCreationType.FromUpload &&
                  !procedureDetails.videoBlob)
              }
              onClick={state.actionButtonHandler}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
